import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import React, { useContext, useEffect, useState } from "react";

import { Edit } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import InfoIcon from "@mui/icons-material/Info";
import {
  Button,
  CardContent,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from "@mui/material";
import { Stack } from "@mui/system";

import { PublisherType } from "@safetyculture/s12-apis-connect-web/s12/contentlibrary/v1/publisher_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import {
  createPublisher,
  getPublisher,
  updatePublisher,
} from "api/endpoints/content-library";
import { DisplayToastAlert } from "components/shared/display-toast-alert";
import { PermissionsContext } from "context/permissions-context";
import { toUUID } from "helpers/s12-helpers";

interface Props {
  roleId: string;
  orgName: string;
}

const slugRegex = /[^a-z0-9\-]+/g;

const OrgContentLibraryTab = (props: Props) => {
  const orgUUID = toUUID(props.roleId);

  const [isPublisherDataError, setIsPublisherDataError] = useState(false);
  const [isPublisher, setIsPublisher] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditLocked, setIsEditLocked] = useState(true);
  const {
    data: publisherResponse,
    isLoading: isLoadingPublisher,
    error: publisherError,
  } = useQuery({
    queryKey: ["getPublisher", orgUUID],
    queryFn: () => getPublisher(orgUUID),
  });

  const queryClient = useQueryClient();
  const createMutation = useMutation({ mutationFn: createPublisher });
  const updateMutation = useMutation({ mutationFn: updatePublisher });

  const autoSlug = React.useMemo(() => {
    if (!props.orgName) {
      return "";
    }
    return props.orgName.toLowerCase().replace(slugRegex, "-");
  }, [props.orgName]);

  const [formState, setFormState] = useState({
    title: props.orgName || "",
    orgId: orgUUID,
    slug: autoSlug,
    publisherType: PublisherType.PARTNERED,
    enabled: true,
  });

  useEffect(() => {
    if (props.orgName && !formState.title) {
      setFormState((prev) => ({
        ...prev,
        title: props.orgName,
        slug: autoSlug,
      }));
    }
  }, [props.orgName, autoSlug, formState.title]);

  useEffect(() => {
    if (!publisherResponse) return;

    if (publisherResponse.publisher?.id) {
      setIsPublisher(true);
      setFormState({
        title: publisherResponse.publisher.title,
        orgId: publisherResponse.publisher.orgId,
        slug: publisherResponse.publisher.slug,
        publisherType: publisherResponse.publisher.publisherType,
        enabled: publisherResponse.publisher.enabled,
      });
    } else if (publisherResponse.publisher && !publisherResponse.publisher.id) {
      setIsPublisherDataError(true);
    }
  }, [publisherResponse]);

  useEffect(() => {
    if (publisherError) {
      console.error("GetPublisher() returned error", publisherError);
    }
  }, [publisherError]);

  const isPartneredType = formState.publisherType === PublisherType.PARTNERED;

  function isSlugInvalid() {
    if (!isPartneredType) return false;
    return !formState.slug || !!formState.slug.match(slugRegex);
  }

  function isCreateDisabled() {
    return !formState.title || isSlugInvalid() || isPublisherDataError;
  }

  const { hasPermissions, permissions } = useContext(PermissionsContext);

  if (permissions.isPermissionsLoading || isLoadingPublisher) {
    return (
      <CardContent>
        <CircularProgress />
      </CardContent>
    );
  }

  const canCreatePublisher = hasPermissions([ActionType.CREATE_PUBLISHER]);
  const canUpdatePublisher = hasPermissions([ActionType.UPDATE_PUBLISHER]);

  function handleCreate() {
    createMutation
      .mutateAsync(formState)
      .then(() => {
        setIsCreating(false);
        setIsPublisher(true);
      })
      .catch(() => {
        // This could be a serious scenario where the DB is out of sync with CT
        // we want this to be addressed properly instead of the user trying it again
        setIsPublisherDataError(true);
      })
      .finally(() => {
        queryClient.invalidateQueries({ queryKey: ["getPublisher", orgUUID] });
      });
  }

  function handleUpdate() {
    updateMutation
      .mutateAsync(formState)
      .then(() => {
        setIsEditLocked(true);
      })
      .catch(() => {
        // Error is surfaced via updateMutation.isError toast
      })
      .finally(() => {
        queryClient.invalidateQueries({ queryKey: ["getPublisher", orgUUID] });
      });
  }

  if (isPublisherDataError && !isCreating) {
    return (
      <CardContent>
        <Typography variant="body1" color="error">
          There is inconsistent data for {props.orgName}. Contact
          #the-librarians before making changes.
        </Typography>
      </CardContent>
    );
  }

  return (
    <>
      <Stack direction="column">
        <CardContent>
          <Typography variant="h6">
            Content Library Publisher Settings
          </Typography>
        </CardContent>

        {!isCreating && !isPublisher && (
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow
                  style={{
                    paddingBottom: "2rem",
                  }}
                >
                  <TableCell align="right" style={{ width: "50%" }}>
                    <Typography
                      variant="body1"
                      style={{ alignContent: "center" }}
                    >
                      {props.orgName} is not currently designated as a
                      publisher.
                    </Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Stack direction="row" alignItems="center">
                      <Button
                        variant="outlined"
                        disabled={!canCreatePublisher || isPublisherDataError}
                        onClick={() => {
                          setIsCreating(true);
                        }}
                      >
                        Enable as publisher
                      </Button>
                      {!canCreatePublisher && (
                        <Tooltip
                          title={"You need Create Publisher permission."}
                        >
                          <InfoIcon />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {isPublisher && (
          <CardContent>
            <Stack direction="row" justifyContent="center">
              {publisherResponse?.publisher?.enabled ? (
                <>
                  <Typography variant="body1">
                    {props.orgName} is designated as a publisher.
                  </Typography>
                  <CheckIcon color="success" />
                </>
              ) : (
                <Typography variant="body1">
                  {props.orgName} is not a publisher.
                </Typography>
              )}
            </Stack>
          </CardContent>
        )}
        {isCreating && (
          <CardContent style={{ borderTop: "2px double" }}>
            <Stack direction="column">
              <Typography variant="h6">Fill in publisher details</Typography>
              <Stack direction="row" gap="1rem" justifyContent="end">
                <Button
                  variant="contained"
                  disabled={!canCreatePublisher || isCreateDisabled()}
                  loading={createMutation.isPending}
                  onClick={handleCreate}
                >
                  Create publisher
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsCreating(false);
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        )}
        {isPublisher && (
          <CardContent style={{ borderTop: "2px double" }}>
            <Stack direction="column">
              <Typography variant="h6">Primary publisher details</Typography>
              <Stack direction="row" justifyContent="end" alignItems="center">
                {isEditLocked && (
                  <Button
                    startIcon={<Edit fontSize="small" />}
                    variant="outlined"
                    onClick={() => {
                      setIsEditLocked(false);
                    }}
                  >
                    Edit
                  </Button>
                )}
                {!isEditLocked && !canUpdatePublisher && (
                  <Tooltip title={"You need Update Publisher permission."}>
                    <InfoIcon />
                  </Tooltip>
                )}
                {!isEditLocked && (
                  <Button
                    variant="contained"
                    disabled={!canUpdatePublisher}
                    loading={updateMutation.isPending}
                    onClick={handleUpdate}
                  >
                    Update publisher
                  </Button>
                )}
              </Stack>
            </Stack>
          </CardContent>
        )}
        {(isCreating || isPublisher) && (
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell align="right" style={{ width: "50%" }}>
                    <Typography variant="body1">Publisher name*</Typography>
                  </TableCell>
                  <TableCell align="left">
                    <TextField
                      disabled={isPublisher}
                      data-testid="name-input-field"
                      value={formState.title}
                      size="small"
                      onChange={(event) => {
                        setFormState({
                          ...formState,
                          title: event.target.value,
                        });
                      }}
                    />
                    {!formState.title && (
                      <Typography color="error">Name is required.</Typography>
                    )}
                  </TableCell>
                </TableRow>
                {(isPublisher || isPartneredType) && (
                  <TableRow>
                    <TableCell align="right">
                      <Typography variant="body1">Slug*</Typography>
                    </TableCell>
                    <TableCell align="left">
                      <TextField
                        disabled={isPublisher}
                        data-testid="slug-input-field"
                        size="small"
                        value={formState.slug}
                        onChange={(event) => {
                          setFormState({
                            ...formState,
                            slug: event.target.value,
                          });
                        }}
                      />
                      {isSlugInvalid() && (
                        <Typography color="error">
                          Only dashes &quot;-&quot; and alphanumerical
                          characters are allowed.
                        </Typography>
                      )}
                      {!formState.slug && (
                        <Typography color="error">Slug is required.</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell align="right">
                    <Typography variant="body1">Publisher type*</Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Stack direction="row" gap="1rem" alignItems="center">
                      <Select
                        disabled={isPublisher && isEditLocked}
                        onChange={(event) => {
                          const newType = Number(
                            event.target.value
                          ) as PublisherType;
                          setFormState({
                            ...formState,
                            publisherType: newType,
                          });
                        }}
                        value={formState.publisherType}
                        size="small"
                      >
                        {!isCreating && (
                          <MenuItem
                            value={PublisherType.UNREGISTERED}
                            key="unregistered"
                          >
                            Unregistered
                          </MenuItem>
                        )}
                        {!isCreating && (
                          <MenuItem
                            value={PublisherType.CUSTOMER}
                            key="customer"
                          >
                            Customer
                          </MenuItem>
                        )}
                        <MenuItem
                          value={PublisherType.PARTNERED}
                          key="partnered"
                        >
                          Partnered
                        </MenuItem>
                        {!isCreating && (
                          <MenuItem
                            value={PublisherType.PROMOTED_CUSTOMER}
                            key="promoted-customer"
                          >
                            Promoted customer
                          </MenuItem>
                        )}
                      </Select>
                      <Typography variant="subtitle2">
                        Partnered customer publishers have a blue tick next to
                        their name.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="right">
                    <Typography variant="body1">Enabled*</Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Select
                      disabled={isPublisher && isEditLocked}
                      onChange={(event) => {
                        setFormState({
                          ...formState,
                          enabled: event.target.value === "true",
                        });
                      }}
                      value={formState.enabled}
                      size="small"
                    >
                      <MenuItem value="true" key="true">
                        YES
                      </MenuItem>
                      <MenuItem value="false" key="false">
                        NO
                      </MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
      <DisplayToastAlert
        message="Publisher created!"
        open={createMutation.isSuccess}
        severity="success"
        onClose={() => createMutation.reset()}
      />
      <DisplayToastAlert
        message="Failed to create publisher. Contact #the-librarians as the system could be in an invalid state."
        open={createMutation.isError}
        severity="error"
        onClose={() => createMutation.reset()}
      />
      <DisplayToastAlert
        message="Publisher updated!"
        open={updateMutation.isSuccess}
        severity="success"
        onClose={() => updateMutation.reset()}
      />
      <DisplayToastAlert
        message="Failed to update publisher. Try again later."
        open={updateMutation.isError}
        severity="error"
        onClose={() => updateMutation.reset()}
      />
    </>
  );
};

export default OrgContentLibraryTab;
